import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, Search } from "lucide-react";

export default function Helpers() {
  const [list, setList] = useState([]);
  const [city, setCity] = useState("");
  const [skill, setSkill] = useState("");

  useEffect(() => {
    const params = {};
    if (city) params.city = city;
    if (skill) params.skill = skill;
    api.get("/laborers", { params }).then(({ data }) => setList(data));
  }, [city, skill]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight">Find a helper</h1>
        <p className="text-slate-600 mt-1">Browse trusted local helpers by skill and city.</p>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200/80 p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Filter by city…" value={city} onChange={(e) => setCity(e.target.value)} className="pl-9 h-11" data-testid="helpers-city-input" />
        </div>
        <Input placeholder="Filter by skill (e.g., painting)" value={skill} onChange={(e) => setSkill(e.target.value)} className="h-11" data-testid="helpers-skill-input" />
      </div>

      {list.length === 0 ? (
        <div className="text-center py-16 text-slate-500">No helpers found.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {list.map((l, i) => (
            <Link key={l.user_id} to={`/helpers/${l.user_id}`} data-testid={`helper-card-${l.user_id}`} className={`bg-white border border-slate-200/80 rounded-2xl p-5 card-hover float-in stagger-${(i % 4) + 1}`}>
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12">
                  <AvatarImage src={l.avatar_url || undefined} />
                  <AvatarFallback className="bg-[#3E2A4E] text-[#D9FF3D] font-bold">{l.name?.[0]}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <div className="font-heading font-bold text-slate-900 truncate">{l.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} /> {l.city || "—"}</div>
                </div>
              </div>
              <p className="text-sm text-slate-600 mt-3 line-clamp-2">{l.bio || "Available for local jobs."}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(l.skills || []).slice(0, 3).map((s) => <Badge key={s} variant="outline" className="rounded-full text-xs">{s}</Badge>)}
              </div>
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-600 inline-flex items-center gap-1">
                  <Star size={13} className="fill-amber-400 text-amber-400" />
                  {l.review_count > 0 ? `${l.avg_rating} · ${l.review_count} reviews` : "No reviews yet"}
                </div>
                {l.hourly_rate && <div className="font-mono-money text-xs text-slate-900">${l.hourly_rate}/hr</div>}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
